import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';
import { queryKeys } from '../../shared/api/query-keys';

/* ── Types ── */

/**
 * Armazem, espelhando `WarehouseResponseDto`.
 *
 * O campo de estado e `status`, nao `active`. Declarar `active` fazia o selo
 * mostrar "Inativo" em todo armazem e o botao de alternar dizer sempre
 * "Ativar" — chamando sempre `/activate`, de modo que nao havia como
 * desativar nada pela tela.
 */
export interface Warehouse {
  id: string;
  /** Publicado pelo DTO e necessario para abrir contagem (`CreateCountDto`). */
  companyId: string;
  name: string;
  code: string;
  status: 'ACTIVE' | 'INACTIVE';
  createdAt: string;
}

/** Leitura derivada do que o servidor publica. */
export function isWarehouseActive(w: Pick<Warehouse, 'status'>): boolean {
  return w.status === 'ACTIVE';
}

export interface WarehouseInput {
  name: string;
  code: string;
}

/** Tipos aceitos na criacao (o backend resolve TRANSFER/ADJUSTMENT em par). */
export type MovementTypeInput = 'RECEIVE' | 'ISSUE' | 'TRANSFER' | 'ADJUSTMENT' | 'COUNT';

/** Tipos que aparecem no registro gravado. */
export type MovementType =
  | MovementTypeInput
  | 'TRANSFER_IN'
  | 'TRANSFER_OUT'
  | 'ADJUSTMENT_IN'
  | 'ADJUSTMENT_OUT';

/** Espelha `StockMovementResponseDto`. */
export interface Movement {
  id: string;
  tenantId: string;
  companyId: string;
  warehouseId: string;
  productId: string;
  type: MovementType;
  quantity: number;
  unitCost: number;
  totalCost: number;
  lotNumber: string | null;
  serialNumber: string | null;
  originType: string;
  originId: string;
  reference: string;
  notes: string | null;
  createdById: string;
  createdAt: string;
}

export interface MovementInput {
  companyId: string;
  warehouseId: string;
  productId: string;
  type: MovementTypeInput;
  quantity: number;
  unitCost?: number;
  lotNumber?: string;
  serialNumber?: string;
  reference?: string;
  notes?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

/* Espelha `BatchResponse`. O lote nao pertence a um armazem no dominio (a
   posicao por deposito vive no saldo de estoque), entao `warehouseId` saiu:
   era campo que nunca chegou. */
export interface Batch {
  id: string;
  tenantId: string;
  companyId: string;
  productId: string;
  batchNumber: string;
  quantity: number;
  expiryDate: string;
  manufacturingDate: string | null;
  status: string;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

/** `BatchTraceResponse`: o lote e as series que sairam dele. */
export interface BatchTracking {
  batch: Batch;
  serialNumbers: Serial[];
}

/** Espelha `SerialResponse`. */
export interface Serial {
  id: string;
  tenantId: string;
  companyId: string;
  productId: string;
  serialNumber: string;
  batchId: string | null;
  status: string;
  soldAt: string | null;
  soldTo: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

/** `SerialTraceResponse`: a serie e o lote de origem, quando houver. */
export interface SerialTracking {
  serial: Serial;
  batch: Batch | null;
}

/**
 * Historico de movimento de um lote.
 *
 * Nao existe endpoint de "historico do lote": o que existe — e e melhor — e o
 * proprio kardex filtrado por `lotNumber`, agora que o filtro foi acrescentado
 * no backend. Cada linha e um movimento real, com referencia do documento.
 */
export function useLotMovements(lotNumber: string) {
  return useQuery({
    queryKey: [...queryKeys.inventory.movements(), 'lot', lotNumber],
    queryFn: () =>
      apiClient.get<PaginatedResponse<Movement>>('/inventory/movements', {
        params: { lotNumber, limit: '100' },
      }),
    enabled: !!lotNumber,
  });
}

/** Espelha `CountItemResponseDto`. */
export interface CountItem {
  productId: string;
  /** Saldo do sistema no momento da contagem. Era lido como `systemQuantity`. */
  expectedQuantity: number;
  countedQuantity: number;
  /** Contado menos esperado. Era lido como `difference`. */
  discrepancy: number;
  unitCost: number;
  lotNumber: string | null;
  serialNumber: string | null;
}

/**
 * Espelha `InventoryCountResponseDto`.
 *
 * A contagem e **de armazem, com itens** — nao de um produto. O tipo plano
 * anterior nao existia no backend em nenhum campo.
 */
export interface Count {
  id: string;
  tenantId: string;
  companyId: string;
  warehouseId: string;
  status: string;
  items: CountItem[];
  countedById: string;
  approvedById: string | null;
  notes: string | null;
  hasDiscrepancies: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Espelha `CountItemDto`. */
export interface CountItemInput {
  productId: string;
  expectedQuantity: number;
  countedQuantity: number;
  unitCost: number;
  lotNumber?: string;
  serialNumber?: string;
}

/** Espelha `CreateCountDto` (companyId sai — sessão). */
export interface CountInput {
  warehouseId: string;
  items: CountItemInput[];
  notes?: string;
}

/** Espelha `PaginatedCountResponseDto`. */
export interface PaginatedCounts {
  data: Count[];
  total: number;
  page: number;
  limit: number;
}

/* ── Reservation Types ── */

export type ReservationStatus = 'ACTIVE' | 'CONSUMED' | 'CANCELLED' | 'EXPIRED';

export interface Reservation {
  id: string;
  tenantId: string;
  companyId: string;
  warehouseId: string;
  productId: string;
  quantity: number;
  originType: string;
  originId: string;
  status: ReservationStatus;
  expiresAt: string | null;
  createdById: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateReservationInput {
  warehouseId: string;
  productId: string;
  quantity: number;
  originType: string;
  originId: string;
  expiresAt?: string;
}

export interface LedgerEntry {
  id: string;
  productId: string;
  warehouseId: string;
  quantity: number;
  type: string;
  reference: string;
  createdAt: string;
}

/* ── Warehouse Hooks ── */

export function useWarehouses() {
  return useQuery({
    queryKey: queryKeys.inventory.warehouses(),
      /* O backend devolve envelope paginado; a tela consome array.
         Extrair `.data` aqui mantem o tipo publico do hook. */
    queryFn: async () =>
      (await apiClient.get<{ data: Warehouse[] }>('/inventory/warehouses')).data,
  });
}

export function useCreateWarehouse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: WarehouseInput) =>
      apiClient.post<Warehouse>('/inventory/warehouses', input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.warehouses() }).catch(() => {});
    },
  });
}

export function useUpdateWarehouse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...input }: WarehouseInput & { id: string }) =>
      apiClient.patch<Warehouse>(`/inventory/warehouses/${id}`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.warehouses() }).catch(() => {});
    },
  });
}

export function useToggleWarehouse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      active
        ? apiClient.post<Warehouse>(`/inventory/warehouses/${id}/activate`)
        : apiClient.post<Warehouse>(`/inventory/warehouses/${id}/deactivate`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.warehouses() }).catch(() => {});
    },
  });
}

export function useDeleteWarehouse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiClient.delete<void>(`/inventory/warehouses/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.warehouses() }).catch(() => {});
    },
  });
}

/* ── Movement Hooks ── */

export function useMovements(params?: { page?: number; limit?: number }) {
  return useQuery({
    queryKey: [...queryKeys.inventory.movements(), params],
    queryFn: () =>
      apiClient.get<PaginatedResponse<Movement>>('/inventory/movements', {
        params: {
          ...(params?.page !== undefined ? { page: String(params.page) } : {}),
          ...(params?.limit !== undefined ? { limit: String(params.limit) } : {}),
        },
      }),
  });
}

export function useCreateMovement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: MovementInput) =>
      apiClient.post<Movement>('/inventory/movements', input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.movements() }).catch(() => {});
    },
  });
}

/* ── Batch & Serial Hooks ── */

/** Linha da listagem de lotes: `BatchResponse` do backend. */
export interface BatchRow {
  id: string;
  productId: string;
  batchNumber: string;
  quantity: number;
  expiryDate: string;
  manufacturingDate: string | null;
  status: 'ACTIVE' | 'EXPIRED' | 'BLOCKED';
  notes: string | null;
}

export interface BatchList {
  data: BatchRow[];
  total: number;
  counts: { active: number; expiringSoon: number; blocked: number };
  expiringInDays: number;
}

/**
 * Lista de lotes com os contadores do topo da tela.
 *
 * `GET /inventory/batches` foi acrescentado no backend nesta rodada: antes
 * existia apenas consulta por numero de lote, e por isso a tela era so busca —
 * nao dava para listar nem contar.
 */
export function useBatches(params?: { status?: string; expiringInDays?: number }) {
  const qs: Record<string, string> = {};
  if (params?.status) qs['status'] = params.status;
  if (params?.expiringInDays) qs['expiringInDays'] = String(params.expiringInDays);
  return useQuery({
    queryKey: [...queryKeys.inventory.batches(), 'list', params ?? {}],
    queryFn: () => apiClient.get<BatchList>('/inventory/batches', { params: qs }),
  });
}

export function useBatchTracking(batchNumber: string) {
  return useQuery({
    queryKey: [...queryKeys.inventory.batches(), batchNumber],
    queryFn: () => apiClient.get<BatchTracking>(`/inventory/batches/${batchNumber}/track`),
    enabled: !!batchNumber,
  });
}

export function useCreateBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { productId: string; batchNumber: string; expirationDate?: string }) =>
      apiClient.post<Batch>('/inventory/batches', input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.batches() }).catch(() => {});
    },
  });
}

export function useSerialTracking(serialNumber: string) {
  return useQuery({
    queryKey: [...queryKeys.inventory.serials(), serialNumber],
    queryFn: () => apiClient.get<SerialTracking>(`/inventory/serials/${serialNumber}/track`),
    enabled: !!serialNumber,
  });
}

export function useCreateSerial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { productId: string; serialNumber: string }) =>
      apiClient.post<Serial>('/inventory/serials', input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.serials() }).catch(() => {});
    },
  });
}

/* ── Count Hooks ── */

/**
 * Saldo por armazem e produto — `GET /inventory/balance`.
 *
 * E a fonte do "esperado" e do custo unitario da contagem ciclica. Nao ha
 * rota de listagem de contagens (lacuna declarada), entao a tela se monta
 * sobre o saldo, que existe.
 */
export interface StockBalance {
  id: string;
  tenantId: string;
  companyId: string;
  warehouseId: string;
  productId: string;
  onHand: number;
  reserved: number;
  available: number;
  lotNumber: string | null;
  serialNumber: string | null;
  expirationDate: string | null;
  unitCost: number;
  version: number;
}

export function useStockBalance() {
  return useQuery({
    queryKey: ['inventory', 'balance'],
    queryFn: () => apiClient.get<StockBalance[]>('/inventory/balance'),
  });
}

/* `GET /inventory/counts` NAO existe — lacuna declarada em
   `api-routes-known-gaps.json`. O hook que a chamava foi removido: chamada
   para rota inexistente e o defeito que manteve a tela de auditoria vazia por
   toda a vida do projeto (todo24 T54). */

export function useCreateCount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CountInput) =>
      apiClient.post<Count>('/inventory/counts', input),
    onSuccess: () => {
      // O ajuste da contagem move o saldo.
      queryClient.invalidateQueries({ queryKey: ['inventory', 'balance'] }).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ['inventory', 'counts'] }).catch(() => {});
    },
  });
}

export function useCounts(params?: { warehouseId?: string; page?: number; limit?: number }) {
  return useQuery({
    queryKey: ['inventory', 'counts', params],
    queryFn: () =>
      apiClient.get<PaginatedCounts>('/inventory/counts', {
        params: {
          ...(params?.warehouseId ? { warehouseId: params.warehouseId } : {}),
          page: String(params?.page ?? 1),
          limit: String(params?.limit ?? 20),
        },
      }),
  });
}

/* ── Reservation Hooks ── */

export function useReservations(params?: { page?: number; limit?: number; warehouseId?: string; productId?: string; status?: string }) {
  return useQuery({
    queryKey: [...queryKeys.inventory.reservations(), params],
    queryFn: () =>
      apiClient.get<PaginatedResponse<Reservation>>('/inventory/reservations', {
        params: {
          ...(params?.page !== undefined ? { page: String(params.page) } : {}),
          ...(params?.limit !== undefined ? { limit: String(params.limit) } : {}),
          ...(params?.warehouseId ? { warehouseId: params.warehouseId } : {}),
          ...(params?.productId ? { productId: params.productId } : {}),
          ...(params?.status ? { status: params.status } : {}),
        },
      }),
  });
}

export function useCreateReservation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateReservationInput) =>
      apiClient.post<Reservation>('/inventory/reservations', input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.reservations() }).catch(() => {});
    },
  });
}

export function useReleaseReservation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      apiClient.delete<Reservation>(`/inventory/reservations/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.reservations() }).catch(() => {});
    },
  });
}
